import React, { ReactNode, ErrorInfo } from 'react';
import logger from '../../../../../utils/logger.utils';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error Boundary para o componente UnicoCapture
 * Captura erros do SDK Unico e previne que apareçam como Uncaught Runtime Error
 *
 * O SDK Unico pode lançar erros internos (isReadyTime) que não conseguem ser
 * tratados no hook porque são assíncronos e escapam do try-catch.
 */
export class UnicoCaptureBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Se for erro do SDK Unico, não muda o estado (renderiza normalmente)
    if (error.message?.includes('isReadyTime') || error.message?.includes('UnicoException')) {
      return { hasError: false };
    }
    // Para outros erros, marca como erro
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Se for erro do SDK Unico, apenas log sem propagar
    if (error.message?.includes('isReadyTime') || error.message?.includes('UnicoException')) {
      logger.error('SDK Unico internal error', { message: error.message });
      // Força renderizar normalmente (o erro já foi setado no estado do hook)
      this.setState({ hasError: false });
    } else {
      logger.error('UnicoCapture Error Boundary', {
        message: error.message,
        stack: errorInfo.componentStack
      });
    }
  }

  render() {
    // Sempre renderiza os children (mesmo com erro do SDK)
    // O componente UnicoCapture vai exibir o erro no Alert
    return this.props.children;
  }
}
